import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  CircularProgress,
  Typography,
  Box,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  InputAdornment,
} from "@mui/material";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  GetApp as DownloadIcon,
  Search as SearchIcon,
  PublicOutlined as GlobeIcon,
  FilterAltOff as ClearIcon,
} from "@mui/icons-material";
import { SuppliersProvider, useSuppliers } from "../../contexts/alfaroojfresh/SuppliersContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ===== Theme =====
const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#f8fafc", paper: "#ffffff" },
    primary: { main: "#f59e0b" },
    text: { primary: "#0f172a", secondary: "#64748b" },
  },
  typography: {
    // fontFamily: "'DM Mono', 'Courier New', monospace",
  },
});

// ===== Animations =====
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.25); }
  50%       { box-shadow: 0 0 0 8px rgba(245,158,11,0); }
`;

// ===== Styled Components =====
const Root = styled("div")({
  minHeight: "100vh",
  background: "#f8fafc",
  // fontFamily: "'DM Mono', monospace",
});

const Main = styled("div")({
  flex: 1,
  padding: "40px 48px",
  animation: `${fadeUp} 500ms ease both`,
});

const Header = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: 32,
});

const TitleBlock = styled("div")({
  "& .eyebrow": {
    fontSize: "0.65rem",
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    color: "#040a2c",
    marginBottom: 6,
  },
  "& h1": {
    margin: 0,
    fontSize: "2rem",
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.03em",
    lineHeight: 1,
  },
  "& .sub": {
    fontSize: "0.75rem",
    color: "#94a3b8",
    marginTop: 6,
  },
});

const ActionBtn = styled(Button)({
  textTransform: "none",
 // // fontFamily: "'DM Mono', monospace",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: 8,
  padding: "8px 16px",
  border: "1px solid #e2e8f0",
  color: "#64748b",
  background: "#ffffff",
  transition: "all 0.2s",
  "&:hover": {
   // borderColor: "#f6f4f00",
    color: "whiteSmoke",
    background: "rgba(245,158,11,0.05)",
  },
});

const FilterBar = styled("div")({
  display: "flex",
  gap: 12,
  marginBottom: 20,
  alignItems: "center",
  flexWrap: "wrap",
});

const StyledTable = styled(Table)({
  "& .MuiTableCell-root": {
    // fontFamily: "'DM Mono', monospace",
    borderBottom: "1px solid #f1f5f9",
    fontSize: "0.78rem",
  },
  "& .MuiTableCell-head": {
    color: "#94a3b8",
    fontSize: "0.65rem",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    background: "#f8fafc",
    fontWeight: 600,
    padding: "14px 16px",
  },
  "& .MuiTableBody-root .MuiTableRow-root": {
    transition: "background 0.15s",
    "&:hover": { background: "#fefce8" },
    "&:hover .ref-chip": { animation: `${pulse} 1.2s ease infinite` },
  },
  "& .MuiTableBody-root .MuiTableCell-root": {
    color: "#475569",
    padding: "13px 16px",
  },
});

const RefChip = styled("span")({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 5,
  background: "#3753b3",
  border: "1px solid rgba(70, 11, 245, 0.2)",
  color: "whiteSmoke",
  fontSize: "0.72rem",
  letterSpacing: "0.05em",
});

const NameCell = styled("span")({
  color: "#0f172a",
  fontWeight: 600,
});

const CountryBadge = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 10px",
  borderRadius: 20,
  background: "#f1f5f9",
  border: "1px solid #e2e8f0",
  fontSize: "0.72rem",
  color: "#64748b",
});

const EmailTag = styled("span")({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 4,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  fontSize: "0.7rem",
  color: "#64748b",
  marginRight: 4,
});

const StatCard = styled("div")({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "16px 20px",
  minWidth: 120,
  "& .val": {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1,
  },
  "& .lbl": {
    fontSize: "0.65rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#94a3b8",
    marginTop: 4,
  },
});

// ===== Main Component =====
const SuppliersTable = () => {
  const { suppliers, loading, error, classifiedPurchaseOrders } = useSuppliers();

  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("ALL");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Derive unique countries from supplier data
  const countries = useMemo(() => {
    if (!suppliers) return [];
    const unique = [...new Set(suppliers.map((s) => s.country).filter(Boolean))].sort();
    return unique;
  }, [suppliers]);

  const filteredSuppliers = useMemo(() => {
    if (!suppliers) return [];
    return suppliers.filter((supplier) => {
      const matchesSearch = Object.values(supplier).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesCountry =
        countryFilter === "ALL" || supplier.country === countryFilter;
      return matchesSearch && matchesCountry;
    });
  }, [suppliers, searchTerm, countryFilter]);

  const hasActiveFilters = searchTerm || countryFilter !== "ALL";

  const clearFilters = () => {
    setSearchTerm("");
    setCountryFilter("ALL");
    setPage(0);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const exportToExcel = () => {
    const date = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const wb = XLSX.utils.book_new();
    const suppliersData = filteredSuppliers.map((sup) => ({
      Ref: `#${sup._id.slice(0, 8)}`,
      Name: sup.name,
      Country: sup.country,
      Code: sup.code,
      Emails: sup.emails.length ? sup.emails.map((i) => i.email).join(", ") : "Empty",
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(suppliersData), "Suppliers");
    const classifiedData =
      classifiedPurchaseOrders?.map((item) => ({
        "Supplier ID":
          item.orders?.flatMap((o) => o.po_inv_items)?.find((p) => p.supplier)?.supplier || "N/A",
        Total: item?.total?.toFixed(2) || 0,
        "Number of Orders": item.orders.length || 0,
      })) || [];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(classifiedData), "PurchaseOrdersBySuppliers");
    XLSX.writeFile(wb, `Suppliers_Export_${date}.xlsx`);
  };

  const exportToPDF = () => {
    const date = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const doc = new jsPDF();
    doc.text(`Suppliers List — ${new Date().toLocaleDateString()}`, 20, 10);
    autoTable(doc, {
      head: [["Ref", "Name", "Country", "Code", "Emails"]],
      body: filteredSuppliers.map((sup) => [
        `#${sup._id.slice(0, 8)}`,
        sup.name,
        sup.country,
        sup.code,
        sup.emails.length ? sup.emails.map((i) => i.email).join(", ") : "Empty",
      ]),
      startY: 20,
    });
    doc.addPage();
    doc.text("Purchase Orders by Suppliers", 20, 10);
    autoTable(doc, {
      head: [["Supplier ID", "Total", "Number of Orders"]],
      body:
        classifiedPurchaseOrders?.map((item) => [
          item.orders?.flatMap((o) => o.po_inv_items)?.find((p) => p.supplier)?.supplier || "N/A",
          item?.total?.toFixed(2) || 0,
          item.orders.length || 0,
        ]) || [],
      startY: 20,
    });
    doc.save(`Suppliers_Export_${date}.pdf`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" sx={{ background: "#f8fafc" }}>
        <CircularProgress sx={{ color: "#f59e0b" }} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" sx={{ background: "#f8fafc" }}>
        <Typography sx={{ color: "#ef4444" }}>Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Root>
      {/* Main Content */}
      <Main>
        <Header>
          <TitleBlock>
            <div className="eyebrow">Procurement</div>
            <h1>Suppliers</h1>
            <div className="sub">
              {filteredSuppliers.length} of {suppliers?.length ?? 0} entries
              {countryFilter !== "ALL" && ` · ${countryFilter}`}
            </div>
          </TitleBlock>

          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            {/* Stat cards */}
            <StatCard>
              <div className="val">{suppliers?.length ?? 0}</div>
              <div className="lbl">Total</div>
            </StatCard>
            <StatCard>
              <div className="val">{countries.length}</div>
              <div className="lbl">Countries</div>
            </StatCard>

            <Box display="flex" flexDirection="column" gap={0.1} ml={1}>
              <ActionBtn startIcon={<DownloadIcon />} onClick={exportToExcel}>
                Excel
              </ActionBtn>
              <ActionBtn startIcon={<DownloadIcon />} onClick={exportToPDF}>
                PDF
              </ActionBtn>
            </Box>
          </Box>
        </Header>

        {/* Filter Bar */}
        <FilterBar>
          <TextField
            placeholder="Search suppliers…"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: 200,
              "& .MuiOutlinedInput-root": {
                // fontFamily: "DM Mono, monospace",
                fontSize: "0.78rem",
                background: "#ffffff",
                borderRadius: 2,
                color: "#0f172a",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": { borderColor: "#3753b3" },
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel
              sx={{
                // fontFamily: "DM Mono, monospace",
                fontSize: "0.75rem",
                color: "#94a3b8",
                "&.Mui-focused": { color: "#3753b3" },
              }}
            >
              Country
            </InputLabel>
            <Select
              value={countryFilter}
              label="Country"
              onChange={(e) => { setCountryFilter(e.target.value); setPage(0); }}
              startAdornment={<GlobeIcon sx={{ color: "#94a3b8", fontSize: 16, mr: 0.5 }} />}
              sx={{
                // fontFamily: "DM Mono, monospace",
                fontSize: "0.78rem",
                background: "#ffffff",
                color: "#0f172a",
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3753b3" },
                "& .MuiSvgIcon-root": { color: "#94a3b8" },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
                    "& .MuiMenuItem-root": {
                      // fontFamily: "DM Mono, monospace",
                      fontSize: "0.78rem",
                      color: "#475569",
                      "&:hover": { background: "#fefce8", color: "#3753b3" },
                      "&.Mui-selected": { background: "rgba(245,158,11,0.08)", color: "#3753b3" },
                    },
                  },
                },
              }}
            >
              <MenuItem value="ALL">All Countries</MenuItem>
              {countries.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {hasActiveFilters && (
            <ActionBtn
              startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
              onClick={clearFilters}
              sx={{ borderColor: "#fecaca", color: "#ef4444", "&:hover": { borderColor: "#ef4444", color: "#ef4444", background: "rgba(239,68,68,0.05)" } }}
            >
              Clear
            </ActionBtn>
          )}
        </FilterBar>

        {/* Table */}
        <TableContainer
          component={Paper}
          sx={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 3,
            boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
          }}
        >
          <StyledTable>
            <TableHead>
              <TableRow>
                <TableCell>Ref</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Emails</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSuppliers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((supplier, index) => (
                  <TableRow key={supplier.id || index}>
                    <TableCell>
                      <RefChip className="ref-chip">#{supplier._id.slice(0, 12)}</RefChip>
                    </TableCell>
                    <TableCell>
                      <NameCell>{supplier.name}</NameCell>
                    </TableCell>
                    <TableCell>
                      <CountryBadge>
                        <GlobeIcon sx={{ fontSize: 11 }} />
                        {supplier.country}
                      </CountryBadge>
                    </TableCell>
                    <TableCell sx={{ color: "#475569 !important", letterSpacing: "0.05em" }}>
                      {supplier.code}
                    </TableCell>
                    <TableCell>
                      {supplier.emails.length
                        ? supplier.emails.map((item, i) => (
                            <EmailTag key={i}>{item.email}</EmailTag>
                          ))
                        : <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>—</span>
                      }
                    </TableCell>
                  </TableRow>
                ))}
              {filteredSuppliers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: "#94a3b8 !important", padding: "40px !important" }}>
                    No suppliers match your filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </StyledTable>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredSuppliers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: "#94a3b8",
            // fontFamily: "DM Mono, monospace",
            fontSize: "0.72rem",
            "& .MuiSelect-icon": { color: "#94a3b8" },
            "& .MuiIconButton-root": { color: "#94a3b8", "&:hover": { color: "#f59e0b" } },
            "& .Mui-disabled": { color: "#e2e8f0 !important" },
          }}
        />
      </Main>
    </Root>
  );
};

const Suppliers = () => (
  <ThemeProvider theme={theme}>
    <SuppliersProvider>
      <SuppliersTable />
    </SuppliersProvider>
  </ThemeProvider>
);

export default Suppliers;